import { asRemoteError } from './remote';

export interface Timers {
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

const defaultTimers: Timers = {
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
  clearTimeout: (h) => globalThis.clearTimeout(h as ReturnType<typeof setTimeout>),
};

export interface RunnerOptions {
  debounceMs?: number;
  minBackoffMs?: number;
  maxBackoffMs?: number;
  timers?: Timers;
  onError?: (err: unknown) => void;
}

/**
 * Serialises sync runs: requests arriving while a run is in flight collapse
 * into one follow-up run; bursts are debounced; transient failures retry
 * with capped exponential backoff.
 */
export class SyncRunner {
  private running: Promise<void> | null = null;
  private again = false;
  private timer: unknown = null;
  private failures = 0;
  private stopped = false;
  private readonly timers: Timers;

  constructor(
    private readonly task: () => Promise<unknown>,
    private readonly opts: RunnerOptions = {},
  ) {
    this.timers = opts.timers ?? defaultTimers;
  }

  /** Schedule a run after `delayMs` (default: the debounce interval). */
  request(delayMs = this.opts.debounceMs ?? 1000): void {
    if (this.stopped) return;
    if (this.timer !== null) this.timers.clearTimeout(this.timer);
    this.timer = this.timers.setTimeout(() => {
      this.timer = null;
      void this.run();
    }, delayMs);
  }

  /** Run now (or right after the current run) and resolve when done. */
  async run(): Promise<void> {
    if (this.stopped) return;
    if (this.running) {
      this.again = true;
      return this.running;
    }
    this.running = (async () => {
      do {
        this.again = false;
        try {
          await this.task();
          this.failures = 0;
        } catch (err) {
          this.opts.onError?.(err);
          if (asRemoteError(err).retryable) {
            this.failures++;
            const base = this.opts.minBackoffMs ?? 2000;
            const max = this.opts.maxBackoffMs ?? 5 * 60_000;
            const delay = Math.min(max, base * 2 ** (this.failures - 1)) * (0.8 + Math.random() * 0.4);
            this.request(delay);
          }
          this.again = false;
        }
      } while (this.again && !this.stopped);
    })();
    try {
      await this.running;
    } finally {
      this.running = null;
    }
  }

  get busy(): boolean {
    return this.running !== null;
  }

  stop(): void {
    this.stopped = true;
    if (this.timer !== null) this.timers.clearTimeout(this.timer);
    this.timer = null;
  }
}
